import { BulkUpdateProgressBanner } from "#src/batteries-included-components/Banners/BulkUpdateProgressBanner/BulkUpdateProgressBanner";
import { BulkDeleteFormSubmissionDialog } from "#src/batteries-included-components/Dialogs/BulkDeleteFormSubmissionsDialog";
import { FormSchemaDropdownInput } from "#src/batteries-included-components/Dropdowns/FormSchemaDropdownInput";
import { UsersDropdownInput } from "#src/batteries-included-components/Dropdowns/UsersDropdownInput";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { getStatusType } from "#src/components/Common/Table/rendererHelper";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { FilterArea } from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useBulkDeleteFormSubmissions } from "#src/components/hooks/adapters/useFormSubmissions";
import { DeviceUpdateStatusBanner } from "#src/components/Organization/Devices/Detail/DeviceUpdateStatusBanner";
import { EquipmentUpdateStatusBanner } from "#src/components/Organization/Facilities/Detail/Equipment/EquipmentUpdateStatusBanner";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToFormSubmissionDetail } from "#src/Routers/links";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { FlowUpdateStatusBanner } from "#src/routes/organization/flows/[flowId]/detail/FlowUpdateStatusBanner";
import { NetworkUpdateStatusBanner } from "#src/routes/organization/networks/[networkId]/detail/NetworkUpdateStatusBanner";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
} from "#src/utils/table";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  DateSelectorInput,
  DropdownInput,
  getDataTableGroupByKeysForGroupByData,
  Icon,
  Pill,
  TextInput,
  type HeaderType,
} from "@validereinc/common-components";
import {
  FormCategoryAdapter,
  FormSubmissionAdapter,
  getFilterObject,
  ResourceDefinitions,
  Resources,
  SortDirection,
  UsersAdapter,
  type AssetTypeType,
  type FormCategoryType,
  type FormSubmissionType,
  type GetListResponseType,
  type GroupedByEntryType,
  type UserType,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import { omit } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import { FacilityUpdateStatusBanner } from "./FacilityUpdateStatusBanner";

const { FormSubmissionStatusOptions } = Forms.logic;

export const AssociatedFormSubmissionsTab = ({
  resourceId,
  resourceType,
  defaultFormCategoryName,
}: {
  resourceId: string;
  resourceType: AssetTypeType;
  defaultFormCategoryName?: string;
}) => {
  const [
    tableConfigStorageKey,
    viewConfigStorageKey,
    filterConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "table-configuration",
      "view-configuration",
      "filter-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    `${resourceType}-${resourceId}-forms`
  );
  const [viewFilters] = useStickyState({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const queryClient = useQueryClient();
  const [selectedFormSubmissions, setSelectedFormSubmissions] = useState<
    Record<string, FormSubmissionType>
  >({});
  const [isSelectionInverted, setIsSelectionInverted] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const queriedFormCategoriesQuery = useQuery({
    queryKey: [
      Resources.FORM_CATEGORY,
      resourceType,
      resourceId,
      { name: defaultFormCategoryName },
    ],
    queryFn: () => {
      return FormCategoryAdapter.getList({
        filters: {
          name: defaultFormCategoryName,
          // @ts-expect-error TODO: use proper filter type
          isAlreadyFormatted: true,
        },
      });
    },
    enabled: !!defaultFormCategoryName,
  });
  const relatedFormCategoriesQuery = useQuery({
    queryKey: [Resources.FORM_CATEGORY, resourceType, resourceId],
    queryFn: () => {
      return FormCategoryAdapter.getList({
        filters: {
          related_entity_id: resourceId,
          related_entity_type: resourceType,
        },
      });
    },
  });

  const filters = useMemo(
    () => ({
      ...toFlattenedObject(viewFilters),
      ...toFlattenedObject(tableFilters),
    }),
    [viewFilters, tableFilters]
  );
  const {
    "created_at.from": createdAtFrom,
    "created_at.to": createdAtTo,
    ...restFilters
  } = filters;
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: "desc",
    },
    filters,
    tableStateStorageKey
  );

  const submissionsGetListParams: Parameters<
    typeof FormSubmissionAdapter.getList
  >["0"] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: {
      ...getFilterObject({
        ...omit(restFilters, ["search"]),
        // @ts-expect-error TODO: Typing on filter
        related_entity_id: resourceId,
        created_at: {
          from: createdAtFrom,
          to: createdAtTo,
        },
      }),
      ...(filters.search
        ? { "form_schema.name": { $like: filters.search } }
        : {}),
      isAlreadyFormatted: true,
    },
  };
  const relatedFormSubmissionsQuery = useQuery({
    queryKey: [
      Resources.FORM_SUBMISSION,
      resourceType,
      resourceId,
      submissionsGetListParams,
    ],
    queryFn: () => {
      return FormSubmissionAdapter.getList(submissionsGetListParams);
    },
    enabled: !!relatedFormCategoriesQuery.data?.data.length,
  });

  const items = relatedFormSubmissionsQuery.data?.data ?? [];
  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...submissionsGetListParams,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map(
      (key) => groupByTablesStates.get(key) ?? submissionsGetListParams
    ),
    (groupedEntry) => groupedEntry.value as string
  );

  const relatedFormSubmissionsGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [
        Resources.FORM_SUBMISSION,
        resourceType,
        resourceId,
        d.payload,
      ],
      queryFn: () => FormSubmissionAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const [bulkDeleteFormSubmissionsJobStorageKey] = useStorageKey(
    ["async-job"],
    "form-submissions-bulk-delete"
  );

  const { handleBulkDelete } = useBulkDeleteFormSubmissions({
    jobKey: bulkDeleteFormSubmissionsJobStorageKey,
    formSubmissionGetListParams: submissionsGetListParams,
    selectedFormSubmissions,
    isSelectionInverted,
    setIsDeleting,
  });

  const metaUserIds = useMemo(
    () =>
      relatedFormSubmissionsQuery.data
        ? Array.from(
            new Set(
              relatedFormSubmissionsQuery.data.data.flatMap((sub) => [
                sub.created_by,
                sub.updated_by,
              ])
            )
          )
        : [],
    [relatedFormSubmissionsQuery]
  );
  const metaUsersQuery = useQuery({
    queryKey: [Resources.USER, { filters: { id: metaUserIds } }],
    queryFn: () => {
      return UsersAdapter.getList({
        filters: {
          id: metaUserIds as string[],
        },
      });
    },
    enabled: !!metaUserIds.length,
  });
  const metaUsersMap = useMemo<Record<string, UserType>>(
    () =>
      metaUsersQuery.data?.data.reduce((map, usr) => {
        map[usr.id] = usr;
        return map;
      }, {}) ?? {},
    [metaUsersQuery.data]
  );
  const relatedFormCategoriesAsDropdownOptions = useMemo(
    () =>
      relatedFormCategoriesQuery.data?.data.map((cat) => ({
        label: cat.name,
        value: cat.id,
      })),
    [relatedFormCategoriesQuery.data?.data]
  );

  const [_isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  const isLoading =
    relatedFormCategoriesQuery.isLoading ||
    (relatedFormSubmissionsQuery.fetchStatus !== "idle" &&
      relatedFormSubmissionsQuery.isLoading) ||
    (queriedFormCategoriesQuery.fetchStatus !== "idle" &&
      queriedFormCategoriesQuery.isLoading) ||
    (metaUsersQuery.fetchStatus !== "idle" && metaUsersQuery.isLoading) ||
    isDeleting;

  const headers: Array<HeaderType<FormSubmissionType>> = useMemo(
    () => [
      {
        label: "Submission Name",
        key: "id",
        renderComponent: ({ item }) =>
          item?.form_schema && item.id ? (
            <RoutingLink to={linkToFormSubmissionDetail(item?.id)}>
              {`${item?.form_schema?.name} - ${item?.id?.slice(0, 7)}`}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      {
        label: "Form Template",
        key: "form_schema.id",
        isGroupable: true,
        renderComponent: ({ item }) => {
          return item.form_schema_id && item.form_schema?.form_category?.id ? (
            <RoutingLink
              to={linkToFormTemplateDetail(
                item.form_schema?.form_category.id,
                item.form_schema_id
              )}
            >
              {item.form_schema?.name ?? "Unknown"}
            </RoutingLink>
          ) : (
            "-"
          );
        },
      },
      {
        label: "Form Category",
        key: "form_schema.form_category.name",
        renderComponent: ({ item }) => {
          return item.form_schema?.form_category?.id ? (
            <RoutingLink
              to={linkToFormCategoryDetails(item.form_schema?.form_category.id)}
            >
              {item.form_schema?.form_category?.name ?? "Unknown"}
            </RoutingLink>
          ) : (
            "-"
          );
        },
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={getStatusType(item.status, "form").type}
            isLoading={isLoading}
          >
            {toTitleCaseString(item.status, { intent: "ui" })}
          </Pill>
        ),
      },
      {
        label: "Submitted By",
        key: "created_by",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) =>
          item.created_by ? (
            <UserDataDisplay
              id={item.created_by}
              name={metaUsersMap[item.created_by]?.name ?? "n/a"}
              isLoading={isLoading}
            />
          ) : (
            "-"
          ),
      },
      {
        label: "Submission Date",
        key: "created_at",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.created_at}
            withTime
          />
        ),
      },
    ],
    [metaUsersMap, isLoading]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: relatedFormSubmissionsGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });

  const relatedFormCategoryIDs =
    relatedFormCategoriesQuery.data?.data.map((entry) => entry.id) ?? [];

  return (
    <>
      <EquipmentUpdateStatusBanner />
      <FacilityUpdateStatusBanner />
      <FlowUpdateStatusBanner />
      <DeviceUpdateStatusBanner />
      <NetworkUpdateStatusBanner />
      <FilterArea.Root
        storageKey={viewConfigStorageKey}
        defaultValues={async () => {
          if (!defaultFormCategoryName) {
            return {
              created_at: DEFAULT_DATE_RANGES.lastThreeMonths,
            };
          }

          const queriedFormCategoryQueryFromCache = queryClient.getQueryData<
            GetListResponseType<FormCategoryType>
          >([
            Resources.FORM_CATEGORY,
            resourceType,
            resourceId,
            {
              name: defaultFormCategoryName,
            },
          ]);
          let defaultFormCategoryId = "";

          if (queriedFormCategoryQueryFromCache?.data) {
            defaultFormCategoryId =
              queriedFormCategoryQueryFromCache.data[0]?.id;
          } else {
            const queriedFormCategories = await FormCategoryAdapter.getList({
              filters: {
                name: defaultFormCategoryName,
                // @ts-expect-error TODO: use proper filter type
                isAlreadyFormatted: true,
              },
            });

            queryClient.setQueryData(
              [
                Resources.FORM_CATEGORY,
                resourceType,
                resourceId,
                {
                  name: defaultFormCategoryName,
                },
              ],
              queriedFormCategories
            );

            defaultFormCategoryId = queriedFormCategories.data[0]?.id;
          }

          return {
            created_at: DEFAULT_DATE_RANGES.lastThreeMonths,
            "form_category.id": defaultFormCategoryId,
          };
        }}
        applyDefaultValues
        shouldPrioritizeStoredFiltersWhenApplyingDefaultValues
      >
        <FilterArea.Container aria-label="View filters for Form Submissions">
          <FilterArea.Content>
            {({ handleOnChange }) => (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <DropdownInput
                  label="Status"
                  name="status"
                  options={FormSubmissionStatusOptions}
                  placeholder="Select Status..."
                  labelKey="label"
                  valueKey="value"
                  isInline
                  isMulti
                  isSearchable
                  isLabelShown={false}
                  isOptionalTextShown={false}
                  onChange={(val) => handleOnChange(val, "status")}
                />
                <DropdownInput
                  name="form_category.id"
                  label="Form Category"
                  placeholder="Select Category..."
                  description="Search by Form Category (only associated categories are shown)"
                  options={relatedFormCategoriesAsDropdownOptions}
                  labelKey="label"
                  valueKey="value"
                  isMulti
                  isInline
                  isOptionalTextShown={false}
                  isLabelShown={false}
                  onChange={(val) => handleOnChange(val, "form_category.id")}
                />
                <FormSchemaDropdownInput
                  name="form_schema.id"
                  label="Form Template"
                  isLabelShown={false}
                  isInline
                  fetchFilters={{
                    status: "active",
                    form_category_id: relatedFormCategoryIDs,
                  }}
                  isDisabled={!relatedFormCategoryIDs.length}
                  placeholder="Select Templates..."
                  description="Search by Form Template"
                  onChange={(val) => {
                    handleOnChange(val, "form_schema.id");
                  }}
                  isOptionalTextShown={false}
                  isMulti
                />
                <DateSelectorInput
                  name="created_at"
                  variant="day"
                  max={new Date()}
                  isRange
                  isInline
                  label="Created At"
                  isLabelShown={false}
                  description="Search by Created At"
                  onChange={(val) => handleOnChange(val, "created_at")}
                />
              </div>
            )}
          </FilterArea.Content>
        </FilterArea.Container>
      </FilterArea.Root>
      <BulkUpdateProgressBanner
        jobStorageKey={bulkDeleteFormSubmissionsJobStorageKey}
        setSelectedItems={setSelectedFormSubmissions}
        setIsRunning={setIsDeleting}
        setIsSelectionInverted={setIsSelectionInverted}
        invalidate={() => {
          queryClient.invalidateQueries({
            queryKey: [Resources.FORM_SUBMISSION],
          });
        }}
        subject={{
          singular: ResourceDefinitions.form_submission.label.singular,
          plural: ResourceDefinitions.form_submission.label.plural,
        }}
        action={"delete"}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Form Submissions",
        }}
        filterComponent={
          <FilterArea.Root storageKey={filterConfigStorageKey}>
            <FilterArea.Container aria-label="Filters for Form Submissions">
              <FilterDrawer.Root>
                <FilterArea.Content>
                  {({ handleOnChange }) => (
                    <TextInput
                      name="search"
                      style={{ marginRight: "8px" }}
                      label="Search"
                      isLabelShown={false}
                      placeholder="Search Submissions..."
                      type="search"
                      autoComplete="on"
                      isInline
                      onChange={(val) => handleOnChange(val, "search")}
                    />
                  )}
                </FilterArea.Content>
                <FilterDrawer.Trigger />
                <FilterDrawer.Content>
                  <UsersDropdownInput
                    name="created_by"
                    label="Submitted By"
                    placeholder="Select a user..."
                    isFluid
                    isMulti
                    isOptionalTextShown={false}
                  />
                </FilterDrawer.Content>
              </FilterDrawer.Root>
            </FilterArea.Container>
          </FilterArea.Root>
        }
        dataTableProps={{
          isLoading: isLoading,
          isBusy:
            relatedFormCategoriesQuery.isFetching ||
            relatedFormSubmissionsQuery.isFetching,
          items,
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            <IsFeatureAvailable
              key="delete"
              featureFlagQuery="core:forms"
              permissionQuery="form_submissions:delete"
              fallbackChildren={null}
            >
              <Button
                {...buttonProps}
                variant="error-outline"
                slotLeft={<Icon variant="trash" />}
                onClick={() => setIsDeleteConfirmationOpen(true)}
              >
                Delete
              </Button>
            </IsFeatureAvailable>,
          ],
          selected: selectedFormSubmissions,
          isSelectionInverted: isSelectionInverted,
          onSelectionChange: (value, shouldInvertSelection) => {
            setSelectedFormSubmissions(value);
            setIsSelectionInverted(shouldInvertSelection ?? false);
          },
          headers,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: relatedFormSubmissionsQuery.data?.total_entries ?? 0,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
        }}
        {...groupByDataTableProps}
      />
      <BulkDeleteFormSubmissionDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
        }}
        handleBulkDelete={handleBulkDelete}
        formSubmissionsCount={
          isSelectionInverted
            ? (relatedFormSubmissionsQuery.data?.total_entries ?? 0)
            : Object.keys(selectedFormSubmissions).length
        }
      />
    </>
  );
};
